import { useMemo } from "react";

import { useNavigate } from "react-router-dom";

import { useTrainingMode } from "./TrainingModeContext";

import { useIsMobile } from "../hooks/useMediaQuery";

import s from "./training.module.css";

const RING_R = 18;

const RING_C = 2 * Math.PI * RING_R;



export default function TrainingCoach() {

  const navigate = useNavigate();

  const {

    active,

    realData,

    currentLesson,

    lessons,

    completed,

    skipped,

    progressCount,

    totalLessons,

    allDone,

    track,

    coachCollapsed,

    setCoachCollapsed,

    onLessonRoute,

    tourSurfaceActive,

    selectLesson,

    skipLesson,

    goNextLesson,

    exitTraining,

  } = useTrainingMode();

  const isMobile = useIsMobile(768);



  const ringOffset = useMemo(() => {

    const ratio = totalLessons ? progressCount / totalLessons : 0;

    return RING_C * (1 - ratio);

  }, [progressCount, totalLessons]);



  if (!active) return null;

  if (isMobile) return null;

  const kicker = realData

    ? "Полное обучение · ваш клуб"

    : track === "full"

      ? "Полное обучение · песочница"

      : "Быстрый старт · песочница";



  const isDone =

    completed.includes(currentLesson?.id) ||

    skipped.includes(currentLesson?.id);



  if (coachCollapsed) {

    if (isMobile && tourSurfaceActive) return null;



    return (

      <div className={`${s.trnRoot} ${s.fabWrap}`}>

        <button

          type="button"

          className={s.fab}

          onClick={() => setCoachCollapsed(false)}

        >

          <span className={s.fabDot} aria-hidden />

          Урок {currentLesson?.index || progressCount}/{totalLessons}

        </button>

      </div>

    );

  }



  const goToStep = () => {

    if (!currentLesson) return;

    selectLesson(currentLesson.id);

  };



  const goNext = () => {

    goNextLesson();

  };



  const doSkip = () => {
    if (!currentLesson?.id) return;
    skipLesson(currentLesson.id);
  };



  const primaryLabel = (() => {

    if (isDone) return "Следующий урок";

    if (tourSurfaceActive) return "Свернуть — смотрю подсказку";

    if (onLessonRoute) return "Показать подсказку";

    return `Открыть: ${currentLesson?.title || "раздел"}`;

  })();



  const primaryAction = () => {

    if (isDone) {

      goNext();

      return;

    }

    if (tourSurfaceActive) {

      setCoachCollapsed(true);

      return;

    }

    goToStep();

  };



  const cardHint = (() => {

    if (isDone) return "Урок завершён — переходите к следующему.";

    if (tourSurfaceActive) {

      return "На экране одна подсказка с кнопкой «Понятно, дальше». Здесь — только список уроков.";

    }

    if (onLessonRoute) {

      return "Нажмите «Показать подсказку», чтобы увидеть тур по этому разделу.";

    }

    return currentLesson?.detail || currentLesson?.hint;

  })();



  return (

    <aside className={`${s.trnRoot} ${s.dock}`} aria-label="Обучение">

      <div className={s.dockHandle} aria-hidden />

      <header className={s.dockHead}>

        <div className={s.dockHeadMain}>

          <div className={s.ring} aria-hidden>

            <svg viewBox="0 0 44 44">

              <circle className={s.ringTrack} cx="22" cy="22" r={RING_R} />

              <circle

                className={s.ringValue}

                cx="22"

                cy="22"

                r={RING_R}

                strokeDasharray={RING_C}

                strokeDashoffset={ringOffset}

              />

            </svg>

            <span className={s.ringLabel}>

              {progressCount}/{totalLessons}

            </span>

          </div>

          <div>

            <p className={s.kicker}>{kicker}</p>

            <h2 className={s.dockTitle}>

              {allDone

                ? "Готово"

                : `Урок ${currentLesson?.index} из ${totalLessons}`}

            </h2>

          </div>

        </div>

        <button

          type="button"

          className={s.iconBtn}

          aria-label="Свернуть"

          onClick={() => setCoachCollapsed(true)}

        >

          −

        </button>

      </header>



      <div className={s.dockBody}>

        {allDone ? (

          <div className={s.card}>

            <h3 className={s.cardTitle}>

              {realData ? "Полное обучение пройдено" : "Вы прошли тренажёр"}

            </h3>

            <p className={s.cardText}>

              {realData

                ? "Вы познакомились с основными разделами. Можно работать в клубе как обычно."

                : "Данные клуба не менялись. Пройдите настройку на реальных данных или добавьте ученика."}

            </p>

            <button

              type="button"

              className={s.btnPrimary}

              onClick={() => {

                exitTraining({

                  message: realData

                    ? "Обучение завершено"

                    : "Обучение завершено. Данные клуба не менялись.",

                });

                navigate("/manage/app");

              }}

            >

              На главную

            </button>

          </div>

        ) : (

          <div className={s.card}>

            <h3 className={s.cardTitle}>{currentLesson?.title}</h3>

            <p className={s.cardText}>{cardHint}</p>

            {isDone ? (

              <p className={s.doneLine}>✓ Сделано — можно дальше</p>

            ) : null}

            <div className={s.actions}>

              <button

                type="button"

                className={

                  tourSurfaceActive && !isDone ? s.btnGhost : s.btnPrimary

                }

                onClick={primaryAction}

              >

                {primaryLabel}

              </button>

              {!isDone && !tourSurfaceActive ? (

                <button type="button" className={s.btnGhost} onClick={doSkip}>

                  Пропустить

                </button>

              ) : null}

            </div>

          </div>

        )}



        <ol className={s.steps}>

          {lessons.map((lesson) => {

            const done = completed.includes(lesson.id);

            const wasSkipped = skipped.includes(lesson.id) && !done;

            const current = lesson.id === currentLesson?.id;

            return (

              <li key={lesson.id}>

                <button

                  type="button"

                  className={[

                    s.step,

                    current ? s.stepCurrent : "",

                    done ? s.stepDone : "",

                    wasSkipped ? s.stepSkip : "",

                  ]

                    .filter(Boolean)

                    .join(" ")}

                  onClick={() => selectLesson(lesson.id)}

                >

                  <span className={s.stepNum}>

                    {done ? "✓" : wasSkipped ? "–" : lesson.index}

                  </span>

                  <span className={s.stepLabel}>{lesson.title}</span>

                </button>

              </li>

            );

          })}

        </ol>

      </div>



      <footer className={s.dockFoot}>

        <p className={s.safeNote}>

          {realData

            ? "Подсказки на реальных данных клуба"

            : "В клуб ничего не записывается"}

        </p>

        <button

          type="button"

          className={s.btnExit}

          onClick={() => {

            exitTraining();

            navigate("/manage/app");

          }}

        >

          Выйти из обучения

        </button>

      </footer>

    </aside>

  );

}


